import { DOCUMENT, ViewportScroller } from '@angular/common';
import {
    Component, Input, Renderer2, Inject, ChangeDetectionStrategy, OnDestroy,
    OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {

    filter, map, tap, switchMap, debounceTime, delay, first,
    distinctUntilChanged,
    takeUntil,
} from 'rxjs/operators';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { getLanguageisRTL, getStore, getHomeConfig } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import {
    findCategoryById,
    getIndexBySection,
    getSectionByIndex,
    isChildOfCategory,
} from '@features/category/application/services/category.utils';
import { SeoSelectors } from '@features/seo';
import { Resolutions } from '@layout/domain/models/layout.models';
import { OpenSearchFromHeader } from '@layout/infrastructure/state/layout.actions';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { HeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { MenuCategory } from '@shared/header/models/category.model';
import { HeaderService } from '@shared/header/services/header.service';
import {
    CloseMenu,
    SetActiveCategoryIdLvl1,
    SetLastSection,
} from '@shared/menu-categories/store/menu-categories.actions';
import {
    getActiveCategories,
    getLastSectionSelector,
    getShowCategoryIdsStatus,
    isMenuOpen,
} from '@shared/menu-categories/store/menu-categories.selectors';
import { GoToHome, GoToSection } from '@shared/navigation';
import { getItemsInCart } from '@shared/shopcart-core/infrastructure/state/shopcart-core.selectors';
import { selectUserIsLogged, selectUserName } from '@shared/user/store';

@Component({
    selector: 'header-menu',
    templateUrl: './header-menu.component.html',
    styleUrls: ['./header-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderMenuComponent implements OnInit, OnDestroy {
    @Input() public categories: MenuCategory[];

    @Input() public direction: string;

    public activeCategories$: Observable<Record<number, any>> = this.store.select(getActiveCategories).pipe(
        filter(data => !!data),
        delay(0),
    );

    public cartItem$: Observable<number> = this.store.select(getItemsInCart).pipe(
        map((coreItemsInCart) => {
            return coreItemsInCart ?? 0;
        }),
    );

    public showCategoryId$: Observable<boolean> = this.store.select(getShowCategoryIdsStatus);

    public lastSection$: Observable<string> = this.store.select(getLastSectionSelector);

    public lastSectionIndex$: Observable<number> = this.lastSection$.pipe(
        map(section => {
            const lastSectionSaved: string | undefined = section;
            const lastSectionStorage: string | undefined = this.storageService.getValue<string>('LAST_SECTION');
            const sectionHeader: string = lastSectionSaved ?? lastSectionStorage ?? 'WOMEN';
            return getIndexBySection(sectionHeader);
        }),
    );

    private closeMenuEnabled = false;

    public isOpenMenu$: Observable<boolean> = this.store.select(isMenuOpen).pipe(
        delay(50),
        tap(isOpen => {
            if (isOpen) {
                setTimeout(() => {
                    this.closeMenuEnabled = true;
                }, 500);
                this.renderer.addClass(this.document.documentElement, 'no-scroll');
            } else {
                setTimeout(() => {
                    this.closeMenuEnabled = false;
                }, 500);
                this.renderer.removeClass(this.document.documentElement, 'no-scroll');
            }
        }),
    );

    public homePagesUrl$: Observable<string[]> = this.store.select(SeoSelectors.getHomeUrls)
        .pipe(filter((val) => !!val), first());

    public isMobile$: Observable<boolean> = this.store.select(getResolution).pipe(
        map((resolution) => {
            return resolution === Resolutions.MOBILE || resolution === Resolutions.TABLET;
        }),
    );

    public readonly username$: Observable<string> = this.store.select(selectUserName).pipe(map(name => {
        const trimmedName = name?.trim();
        return trimmedName !== '-' ? trimmedName : null;
    }));

    public readonly isRtl$: Observable<boolean> = this.store.select(getLanguageisRTL);

    private readonly MARGIN_MENU = 200;

    private readonly scrollAnimation$ = new Subject();

    private readonly scrollAnimationObservable = this.scrollAnimation$.asObservable();

    public readonly scrollToCategory$ = combineLatest([
        this.activeCategories$,
        this.lastSectionIndex$,
    ]).pipe(
        debounceTime(0),
        filter(() => this.platform.isBrowser()),
        map(([activeCategories, index]) => {
            const categoryActive = activeCategories[index];
            const categoryIdLvl1 = categoryActive?.activeCategoryIdLvl1;
            const categoryIdLvl2 = categoryActive?.activeCategoryIdLvl2;
            const categoryLvl1 = findCategoryById(this.categories, categoryIdLvl1);
            const isChild = isChildOfCategory(categoryLvl1, categoryIdLvl2);
            if (isChild) {
                return categoryActive?.activeCategoryIdLvl2 ?? categoryActive?.activeCategoryIdLvl1;
            }
            return categoryActive?.activeCategoryIdLvl1;
        }),
        filter((elementId) => !!elementId),
        switchMap((elementId) => {
            return of(this.document.getElementById(elementId));
        }),
        filter((linkActive) => !!linkActive),
        tap((linkActive) => {
            const container = this.document.querySelector('.mn-inner.show-subcategories-section');

            const activeRect = linkActive.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const offset = activeRect.top - containerRect.top;

            const scrollTo = container.scrollTop + offset - this.MARGIN_MENU;
            this.scrollAnimation$.next(scrollTo);
        }),
    );

    public isAuthenticated$: Observable<boolean> = this.store.select(selectUserIsLogged)
        .pipe(filter((isLogged) => typeof isLogged === 'boolean'));

    public isOpenForSale$: Observable<boolean> = this.store.select(getStore).pipe(
        filter((storeData) => !!storeData),
        map((storeData) => storeData.isOpenForSale),
    );

    public homeSeoUrl$: Observable<string> = combineLatest([
        this.store.select(getHomeConfig),
        this.store.select(getLastSectionSelector),
    ]).pipe(
        map(([configs, section]) => configs.find(config => config.section === section)),
        filter(config => !!config && config.locationPath !== ''),
        map(config => config.locationPath),
    );

    private readonly destroy$ = new Subject();

    public titleLogoDom: string;

    constructor(
        private readonly store: Store,
        private readonly renderer: Renderer2,
        private readonly abTestingService: ABTestingService,
        private readonly viewportScroller: ViewportScroller,
        private readonly storageService: CoreStorageService,
        private readonly platform: CorePlatformService,
        private readonly headerService: HeaderService,
        @Inject(DOCUMENT) private readonly document: Document,
    ) { }

    public ngOnInit(): void {
        if (this.platform.isBrowser()) {
            this.scrollAnimationObservable
                .pipe(
                    distinctUntilChanged(),
                    takeUntil(this.destroy$),
                )
                .subscribe((scrollTo: number) => {
                    this.document.querySelector('.mn-inner.show-subcategories-section').scrollTo({
                        top: scrollTo,
                        behavior: 'smooth',
                    });
                });
        }
        this.titleLogoDom = this.document?.title ?? 'Massimo Dutti';
    }

    public ngOnDestroy(): void {
        if (this.platform.isBrowser()) {
            this.renderer.removeClass(this.document.documentElement, 'no-scroll');
        }

        this.destroy$.next(true);
        this.destroy$.complete();
    }

    public closeMenu(): void {
        if (this.closeMenuEnabled) {
            this.store.dispatch(CloseMenu());
        }
    }

    public openCategory({ category, index, show }): void {
        if (show) {
            this.headerService.openCategory(category, index);
        } else {
            this.store.dispatch(SetActiveCategoryIdLvl1({ activeCategoryIdLvl1: null, index }));
        }
    }

    public openSubCategory({ category }): void {
        this.headerService.openSubCategory(category);
    }

    public openSection(index: number): void {
        this.store.dispatch(SetLastSection({ section: getSectionByIndex(index), navigation: true }));
    }

    public openSearch(): void {
        this.store.dispatch(OpenSearchFromHeader({ from: 'header' }));
    }

    public goUserAccount(isLogged: boolean) {
        if (!isLogged) {
            this.store.dispatch(HeaderEventAnalytics({ section: 'login' }));
            this.store.dispatch(GoToSection({ url: 'userLoginApolo' }));
        } else {
            this.store.dispatch(HeaderEventAnalytics({ section: 'account' }));
            this.store.dispatch(GoToSection({ url: 'myAccount' }));
        }
        this.closeMenu();
    }

    public nothing($event: MouseEvent) {
        $event.preventDefault();
    }

    public goToHome($event?: MouseEvent) {
        if ($event) {
            $event.preventDefault();
            $event.stopPropagation();
        }
        this.closeMenu();
        this.store.dispatch(HeaderEventAnalytics({ section: 'logo' }));
        this.store.dispatch(GoToHome({}));
        this.viewportScroller.scrollToPosition([0, 0]);
    }
}
